import { TFunction } from 'i18next';

import { LcaDiagramColumns } from './Diagram/index.types';

const STAGE_LABEL_KEYS: Record<string, string> = {
  [LcaDiagramColumns.rawMaterialsExtractionAndProcessing]:
    'reportBuilder.stageNames.rawMaterials',
  [LcaDiagramColumns.transportOfRawMaterials]:
    'reportBuilder.stageNames.transport',
  [LcaDiagramColumns.productionAndPackaging]:
    'reportBuilder.stageNames.production',
  [LcaDiagramColumns.distributionAndStorage]:
    'reportBuilder.stageNames.distribution',
  [LcaDiagramColumns.installation]: 'reportBuilder.stageNames.installation',
  [LcaDiagramColumns.useStage]: 'reportBuilder.stageNames.useStage',
  [LcaDiagramColumns.endOfLife]: 'reportBuilder.stageNames.endOfLife',
};

const GROUP_LABEL_KEYS: Record<string, string> = {
  cradle: 'boundaries.cradle',
  grave: 'boundaries.grave',
  gate: 'boundaries.gate',
};

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  'forestry-and-wood': 'process.categoryNames.forestryAndWood',
  'water-supply': 'process.categoryNames.waterSupply',
  'transport-and-machines': 'process.categoryNames.transportAndMachines',
  'land-plants-and-animals': 'process.categoryNames.landPlantsAndAnimals',
  energy: 'process.categoryNames.energy',
  'tourism-services': 'process.categoryNames.tourismServices',
  'chemicals-and-plastics': 'process.categoryNames.chemicalsAndPlastics',
  fuels: 'process.categoryNames.fuels',
  'waste-management-and-recycling':
    'process.categoryNames.wasteManagementAndRecycling',
  textiles: 'process.categoryNames.textiles',
  'pulp-and-paper': 'process.categoryNames.pulpAndPaper',
  metals: 'process.categoryNames.metals',
  'building-and-construction': 'process.categoryNames.buildingAndConstruction',
};

const IMPACT_AREA_LABEL_KEYS: Record<string, string> = {
  climatechange: 'impactAreaNames.climateChange',
  globalwarmingpotential: 'impactAreaNames.globalWarmingPotential',
  carbonfootprint: 'impactAreaNames.carbonFootprint',
  wateruse: 'impactAreaNames.waterUse',
  resourceuse: 'impactAreaNames.resourceUse',
  renewableandnonrenewableprimaryenergyuse: 'impactAreaNames.primaryEnergyUse',
  renewableprimaryenergyuse: 'impactAreaNames.renewablePrimaryEnergyUse',
  nonrenewableprimaryenergyuse: 'impactAreaNames.nonRenewablePrimaryEnergyUse',
  primaryenergyuse: 'impactAreaNames.primaryEnergyUse',
  ozonedepletionpotential: 'impactAreaNames.ozoneDepletionPotential',
  acidificationpotential: 'impactAreaNames.acidificationPotential',
  eutrophicationpotential: 'impactAreaNames.eutrophicationPotential',
  photochemicalozonecreationpotential:
    'impactAreaNames.photochemicalOzoneCreationPotential',
  photochemicalozoneformationpotential:
    'impactAreaNames.photochemicalOzoneCreationPotential',
  impactondeforestation: 'impactAreaNames.deforestation',
  deforestation: 'impactAreaNames.deforestation',
};

const normalizeLabelKey = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '');

const translateLabel = (
  t: TFunction<'lca'>,
  translationKey: string,
  fallbackLabel: string
) => {
  const translatedLabel = t(translationKey);
  return translatedLabel !== translationKey ? translatedLabel : fallbackLabel;
};

export const getLifecycleStageLabel = (
  t: TFunction<'lca'>,
  stageKey: string,
  fallbackLabel?: string
) => {
  const translationKey = STAGE_LABEL_KEYS[stageKey];
  const resolvedFallback = fallbackLabel || stageKey;

  if (!translationKey) {
    return resolvedFallback;
  }

  return translateLabel(t, translationKey, resolvedFallback);
};

export const getLifecycleGroupLabel = (
  t: TFunction<'lca'>,
  groupKey: string,
  fallbackLabel?: string
) => {
  const translationKey = GROUP_LABEL_KEYS[groupKey];
  const resolvedFallback = fallbackLabel || groupKey;

  if (!translationKey) {
    return resolvedFallback;
  }

  return translateLabel(t, translationKey, resolvedFallback);
};

export const getImpactAreaLabel = (
  t: TFunction<'lca'>,
  impactAreaName: string,
  fallbackLabel?: string
) => {
  const resolvedFallback = fallbackLabel || impactAreaName;
  const translationKey =
    IMPACT_AREA_LABEL_KEYS[normalizeLabelKey(impactAreaName)];

  if (!translationKey) {
    return resolvedFallback;
  }

  return translateLabel(t, translationKey, resolvedFallback);
};

export const getLifecycleCategoryLabel = (
  t: TFunction<'lca'>,
  categoryKey: string,
  fallbackLabel?: string
) => {
  const translationKey = CATEGORY_LABEL_KEYS[categoryKey];
  const resolvedFallback = fallbackLabel || categoryKey;

  if (!translationKey) {
    return resolvedFallback;
  }

  return translateLabel(t, translationKey, resolvedFallback);
};

export const getLifecycleSubcategoryLabel = (
  t: TFunction<'lca'>,
  subcategoryName: string,
  fallbackLabel?: string
) => {
  const resolvedFallback = fallbackLabel || subcategoryName;
  const translationKey = `process.subcategoryNames.${normalizeLabelKey(
    subcategoryName
  )}`;

  return translateLabel(t, translationKey, resolvedFallback);
};

export const getLifecycleSubcategoryGroupLabel = (
  t: TFunction<'lca'>,
  groupName: string,
  fallbackLabel?: string
) => {
  const resolvedFallback = fallbackLabel || groupName;
  const translationKey = `process.subcategoryGroups.${normalizeLabelKey(
    groupName
  )}`;

  return translateLabel(t, translationKey, resolvedFallback);
};
